import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IStoreInterface } from '../../../configs/store.config'
import { Formik } from 'formik'
import { createSaleValidationSchema } from '../../../admin/validation.schemas'
import { Container } from '../../../shared/shared.styled'
import { SaleRequest } from '../../sales.interfaces'
import { CreateSaleForm } from '../../forms/create-sale.form'
import { AdminApi } from '../../../admin/admin.api'
import { useNavigate } from 'react-router-dom'
import { VehicleRoutesEnum } from '../../../routes.constants'
import moment from 'moment'
import { LoggedUser, Vehicle, Stand } from '../../../shared/shared.models'
import { useAlert } from 'react-alert'
import { useIsAdmin } from '../../../shared/hooks/use-is-admin.hook'
import { AdminActions } from '../../../admin/admin.store'

interface CreateSalePageProps {
    vehicle: Vehicle
}

export const CreateSalePage = ({ vehicle }: CreateSalePageProps) => {
    // Hooks init
    const navigate = useNavigate()
    const alert = useAlert()
    const isAdmin = useIsAdmin()
    const dispatch = useDispatch()

    const loggedUser = useSelector(
        (state: IStoreInterface) => state.adminReducer.user as LoggedUser
    )

    const { company } = useSelector(
        (state: IStoreInterface) => state.adminReducer
    )
    const [stands, setStands] = React.useState<Stand[]>([])

    React.useEffect(() => {
        try {
            if (company) {
                AdminApi.methods.getStands(company.uuid).then(
                    (res) => {
                        setStands(res.data.stands)
                    },
                    (e) => {
                        alert.error('Error fetching the stands list')
                    }
                )
            }
        } catch (e) {
            alert.error('Erro ao listar os stands. Por favor tente mais tarde')
        }
    }, [alert, company])

    const [isLoading, setIsLoading] = React.useState(false)

    // If the vehicle doesn't exist, we don't show anything
    // In the future, we could show a error page
    if (!vehicle) return null

    const submitValues = (values: SaleRequest) => {
        setIsLoading(true)

        // Transform values: if no loan or loanValue is 0 or empty, set loanValue to undefined
        const transformedValues = { ...values }
        if (!values.loan || values.loanValue === 0) {
            transformedValues.loanValue = undefined
        }

        AdminApi.methods
            .createSale(transformedValues)
            .finally(() => setIsLoading(false))
            .then(
                async () => {
                    alert.success('Venda criada com sucesso')
                    try {
                        const res = await AdminApi.methods.getVehicle(
                            vehicle.uuid
                        )
                        dispatch(
                            AdminActions.methods.updateVehicleSuccessAction(
                                res.data.vehicle
                            )
                        )
                    } catch (e) {
                        console.error('Failed to fetch updated vehicle:', e)
                    }

                    navigate(
                        VehicleRoutesEnum.SALE_DETAILS.replace(
                            ':vehicleId',
                            vehicle.uuid
                        )
                    )
                },
                () => {
                    alert.error(
                        'Houve um problema a criar a venda, por favor tente mais tarde'
                    )
                }
            )
    }

    return (
        <Container>
            <Formik<SaleRequest>
                onSubmit={submitValues}
                validationSchema={createSaleValidationSchema()}
                validateOnChange={false}
                initialValues={{
                    processDate: moment().format('YYYY-MM-DD'),
                    processCreatorUuid: loggedUser.uuid,
                    dateOfSale: '',
                    sellerUuid: !isAdmin ? loggedUser.uuid : '',
                    monthWarranty: undefined,
                    mileageWarranty: undefined,
                    vehicleUuid: vehicle.uuid,
                    salePrice: undefined,
                    effectivePrice: undefined,
                    onSignedContractValue: undefined,
                    onVehicleDeliveryValue: undefined,
                    loan: false,
                    loanValue: undefined,
                    client: {
                        name: '',
                    },
                    origin: undefined,
                }}
            >
                {() => (
                    <CreateSaleForm
                        isLoading={isLoading}
                        submitValues={submitValues}
                        vehicle={vehicle}
                        stands={stands}
                    />
                )}
            </Formik>
        </Container>
    )
}
